import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { DropdownAnchor } from './JourneyPickerDesktop.styles';

const VIEWPORT_MARGIN = 16;

export function ClampedDropdownAnchor({
  align,
  children,
}: {
  align?: 'center' | 'right';
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState(0);

  const clamp = useCallback(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;
    setShift((current) => {
      const rect = el.getBoundingClientRect();
      const left = rect.left - current;
      const right = rect.right - current;
      let next = 0;
      if (right > window.innerWidth - VIEWPORT_MARGIN) {
        next = window.innerWidth - VIEWPORT_MARGIN - right;
      }
      if (left + next < VIEWPORT_MARGIN) {
        next = VIEWPORT_MARGIN - left;
      }
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    clamp();
    const el = ref.current;
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(clamp) : null;
    if (el) observer?.observe(el);
    window.addEventListener('resize', clamp);
    window.addEventListener('scroll', clamp, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', clamp);
      window.removeEventListener('scroll', clamp, true);
    };
  }, [clamp]);

  return (
    <DropdownAnchor
      ref={ref}
      $align={align}
      style={{ '--dropdown-clamp-shift': `${shift}px` } as CSSProperties}
    >
      {children}
    </DropdownAnchor>
  );
}
